import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Download, Share2, QrCode, Nfc, Check, Zap } from "lucide-react";
import { Button } from "@/components/uiDir/button";
import { Input } from "@/components/uiDir/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/uiDir/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/uiDir/tabs";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import type { UserProfile } from "@/lib/models/shareportfolioModel";

export default function SharePortfolio() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [isWriting, setIsWriting] = useState(false);
  const [nfcSupported, setNfcSupported] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const portfolioUrl = userProfile ? `https://portfoliohub-demo.vercel.app/profile/${userProfile.username}` : "";
   const nfcUrl = userProfile
    ? `https://portfoliohub-demo.vercel.app/profile/${userProfile.username}`
    : "";
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
      fetchUserProfile();
    }, []);

  const fetchUserProfile = async () => {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.error('Error fetching user:', userError);
        navigate('/login');
        return;
      }

      // Get user profile from profiles table
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        toast.error('Failed to load profile data');
        setIsLoading(false);
        return;
      }

      setUserProfile({
        ...profile,
        email: user.email || ''
      });
      setIsLoading(false);
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred while loading your profile');
      setIsLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(portfolioUrl);
    setCopied(true);
    toast.success("Link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadQR = () => {
    toast.success("QR code downloaded!");
  };

  const handleCopyNFCUrl = () => {
    navigator.clipboard.writeText(nfcUrl);
    toast.success("NFC URL copied! Use an NFC writer app to encode this to your card.");
  };

  const handleWriteNFC = async () => {
    // Check if Web NFC is supported
    if (!('NDEFReader' in window)) {
      setNfcSupported(false);
      toast.error("NFC is not supported on this device/browser. Please use Chrome on Android or a compatible device.");
      return;
    }

    try {
      setIsWriting(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const NDEFReaderClass = (window as any).NDEFReader;
      const ndef = new NDEFReaderClass();
      
      toast.info("Hold your NFC card near your device...", { duration: 5000 });
      
      await ndef.write({
        records: [{ recordType: "url", data: nfcUrl }]
      });
      
      toast.success("✅ NFC card written successfully! Your portfolio is now on the card.");
      setIsWriting(false);
    } catch (error) {
      console.error("NFC write error:", error);
      setIsWriting(false);
      
      const err = error as Error & { name: string };
      
      if (err.name === 'NotAllowedError') {
        toast.error("NFC permission denied. Please allow NFC access in your browser settings.");
      } else if (err.name === 'NotSupportedError') {
        toast.error("NFC is not supported on this device.");
      } else if (err.name === 'NotReadableError') {
        toast.error("Cannot read NFC tag. Make sure it's not locked and try again.");
      } else {
        toast.error("Failed to write NFC card. Please try again or use an NFC writer app.");
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10 max-w-4xl">
        <div className="mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
          </Link>
          <h1 className="text-4xl font-heading font-bold mb-2">Share Portfolio</h1>
          <p className="text-muted-foreground">Share your portfolio with the world</p>
        </div>

        <Tabs defaultValue="link" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3">
            <TabsTrigger value="link">
              <Share2 className="mr-2 h-4 w-4" />
              Link
            </TabsTrigger>
            <TabsTrigger value="qr">
              <QrCode className="mr-2 h-4 w-4" />
              QR Code
            </TabsTrigger>
            <TabsTrigger value="nfc">
              <Nfc className="mr-2 h-4 w-4" />
              NFC
            </TabsTrigger>
          </TabsList>

          {/* Link Tab */}
          <TabsContent value="link" className="space-y-6">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Your Portfolio Link</CardTitle>
                <CardDescription>Share this link anywhere</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input value={portfolioUrl} readOnly className="flex-1" />
                  <Button onClick={handleCopyLink} variant="outline">
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Button variant="outline" className="w-full" onClick={() => window.open(`https://linkedin.com/share?url=${portfolioUrl}`, '_blank')}>
                    LinkedIn
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => window.open(`https://twitter.com/intent/tweet?url=${portfolioUrl}`, '_blank')}>
                    Twitter
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => window.open(`https://facebook.com/sharer/sharer.php?u=${portfolioUrl}`, '_blank')}>
                    Facebook
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => window.open(`https://wa.me/?text=${portfolioUrl}`, '_blank')}>
                    WhatsApp
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Embed Code</CardTitle>
                <CardDescription>Embed your portfolio on other websites</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-muted p-4 rounded-lg font-mono text-sm overflow-x-auto">
                  <code>{`<iframe src="${portfolioUrl}" width="100%" height="600px"></iframe>`}</code>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* QR Code Tab */}
          <TabsContent value="qr" className="space-y-6">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>QR Code</CardTitle>
                <CardDescription>Download and print your QR code</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex justify-center">
                  <div className="p-8 bg-white rounded-2xl shadow-lg">
                    <div className="w-64 h-64 bg-muted rounded-lg flex items-center justify-center">
                      <QrCode className="h-32 w-32 text-muted-foreground" />
                      <p className="absolute mt-48 text-xs text-muted-foreground">QR Code Preview</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Button onClick={handleDownloadQR} className="w-full bg-gradient-primary">
                    <Download className="mr-2 h-4 w-4" />
                    Download QR Code (PNG)
                  </Button>
                  <Button onClick={handleDownloadQR} variant="outline" className="w-full">
                    <Download className="mr-2 h-4 w-4" />
                    Download QR Code (SVG)
                  </Button>
                </div>

                <div className="bg-muted/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">💡 Usage Tips:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li>Print on business cards</li>
                    <li>Add to your resume</li>
                    <li>Display at events and presentations</li>
                    <li>Share in email signatures</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* NFC Tab */}
          <TabsContent value="nfc" className="space-y-6">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>NFC Card Setup</CardTitle>
                <CardDescription>Write your portfolio to an NFC card</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-primary/10 border border-primary/20 p-6 rounded-lg text-center">
                  <Nfc className="h-16 w-16 mx-auto mb-4 text-primary" />
                  <h3 className="font-semibold text-lg mb-2">Contactless Portfolio Sharing</h3>
                  <p className="text-sm text-muted-foreground">
                    Tap your NFC card on any smartphone to instantly share your portfolio
                  </p>
                </div>

                {/* Instant NFC Writer */}
                <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="h-5 w-5 text-primary" />
                      Instant NFC Writer
                    </CardTitle>
                    <CardDescription>Write directly from your browser (Chrome on Android)</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Button 
                      onClick={handleWriteNFC} 
                      disabled={isWriting}
                      className="w-full bg-gradient-primary text-lg py-6"
                    >
                      {isWriting ? (
                        <>
                          <Nfc className="mr-2 h-5 w-5 animate-pulse" />
                          Hold Card Near Device...
                        </>
                      ) : (
                        <>
                          <Zap className="mr-2 h-5 w-5" />
                          Write to NFC Card Now
                        </>
                      )}
                    </Button>
                    
                    {!nfcSupported && (
                      <div className="bg-yellow-500/10 border border-yellow-500/20 p-3 rounded-lg">
                        <p className="text-sm text-yellow-600 dark:text-yellow-500">
                          ⚠️ Web NFC is only supported on Chrome for Android. Please use a compatible device or use the manual method below.
                        </p>
                      </div>
                    )}

                    <div className="bg-muted/50 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground">
                        ℹ️ Web NFC API works on Chrome/Edge for Android. For iOS or other browsers, use the manual method below.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      Or use manual method
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">NFC URL to Write:</h4>
                    <div className="flex gap-2">
                      <Input value={nfcUrl} readOnly className="flex-1 font-mono text-sm" />
                      <Button onClick={handleCopyNFCUrl} variant="outline">
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="bg-muted/50 p-4 rounded-lg space-y-3">
                    <h4 className="font-semibold">📱 How to Write NFC Card (Manual):</h4>
                    <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                      <li>Purchase blank NFC cards (NTAG213/215/216)</li>
                      <li>Download an NFC writer app:
                        <ul className="ml-6 mt-1 space-y-1">
                          <li>• iOS: <span className="font-semibold">NFC Tools</span></li>
                          <li>• Android: <span className="font-semibold">NFC Tools</span> or <span className="font-semibold">TagWriter</span></li>
                        </ul>
                      </li>
                      <li>Copy the NFC URL above</li>
                      <li>Open the NFC writer app and select "Write"</li>
                      <li>Choose "URL/URI" as the data type</li>
                      <li>Paste your NFC URL</li>
                      <li>Hold your phone near the NFC card to write</li>
                    </ol>
                  </div>

                  <div className="bg-accent/10 border border-accent/20 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">🛒 Where to Buy NFC Cards:</h4>
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                      <li>Amazon: Search "NTAG215 NFC Cards"</li>
                      <li>Lazada/Shopee (Philippines)</li>
                      <li>Local electronics stores</li>
                    </ul>
                    <p className="text-xs text-muted-foreground mt-2">
                       Typical price: ₱20-50 per card
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}